import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ACTION_FAILED_EVENT } from '../lib/actionFeedback';

/**
 * ActionErrorToast — "Couldn't <action> — try again."
 *
 * UX review H7 (card ESidDNa3mmaP): consequential actions failed silently.
 * This is the visible half: it listens for `pear:action-failed` and shows one
 * bottom toast in the same slot and vocabulary as UpdateToast, with an
 * explicit dismiss. It clears itself after a few seconds so a stale failure
 * never parks over the thread.
 *
 * `role="alert"` so a screen reader announces it as it appears, and portalled
 * to body — the app root carries a fade-in transform, and a transformed
 * ancestor becomes the containing block for `position: fixed` (FktjQ7CT4keo,
 * the same lesson UpdateToast and KudosToast document).
 */
const AUTO_DISMISS_MS = 8000;

export function ActionErrorToast() {
  const [what, setWhat] = useState<string | null>(null);

  useEffect(() => {
    let timer: number | null = null;
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent<{ what?: unknown }>).detail;
      const text = typeof detail?.what === 'string' && detail.what ? detail.what : null;
      if (!text) return;
      setWhat(text);
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => setWhat(null), AUTO_DISMISS_MS);
    };
    window.addEventListener(ACTION_FAILED_EVENT, onFail);
    return () => {
      window.removeEventListener(ACTION_FAILED_EVENT, onFail);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, []);

  if (!what) return null;

  return createPortal(
    <div className="pear-action-error" role="alert" data-test="action-error">
      <span className="pear-action-error-text">Couldn't {what} — try again.</span>
      <button
        type="button"
        className="pear-action-error-dismiss"
        aria-label="Dismiss error notice"
        onClick={() => setWhat(null)}
      >
        ✕
      </button>
    </div>,
    document.body,
  );
}
